import { Component, OnInit } from '@angular/core';
import { Store } from '@ngrx/store';
import { AppState } from 'app/app.reducer';
import { isEmpty, eq, join } from 'lodash';
import { DatePipe } from '@angular/common';
import { FILTERS } from 'app/constants';
import {
  resetAdvancedSearchFilters,
  resetAuthorProfile,
  resetOutletProfile,
  resetTopicProfile,
  setAdvancedFilterText,
  unSetAdvancedFilterText,
} from 'app/redux/actions';

@Component({
  selector: 'app-filter-text',
  template: `
    <div class="filter-text">
      <div *ngIf="filterText">
        {{ filterText }}.&nbsp;
        <a
          href="javascript:;"
          role="button"
          (click)="clearFilters()"
          class="a-button-link"
          ><i class="pi pi-filter-slash filter-text"></i> Clear Filters</a
        >
      </div>
    </div>
  `,
  styles: [
    `
      .filter-text {
        font-size: small;
        font-style: italic;
        color: #7b7b7b;
        overflow-wrap: anywhere;
      }
    `,
  ],
  providers: [DatePipe],
})
export class FilterTextComponent implements OnInit {
  public filterText: string;
  public datePipe: any;
  public keywords: string;
  constructor(private store: Store<AppState>) {
    this.filterText = null;
    this.datePipe = new DatePipe('en-US');
    this.keywords = null;
  }

  ngOnInit() {
    this.store.select('advancedSearch').subscribe(({ outletGroups }) => {
      if (outletGroups?.length > 0) {
        const existingFilter = FILTERS.find(
          (filter) => filter.key === 'outletGroups'
        );
        const mappedGroups = outletGroups.map((group) => ({
          label: group.name,
          value: group.id.toString(),
        }));

        if (existingFilter) {
          existingFilter.filters = mappedGroups;
        } else {
          FILTERS.push({
            key: 'outletGroups',
            filters: mappedGroups,
          });
        }
      }
    });
    this.store.select('search').subscribe(({ searchBody }) => {
      if (searchBody?.keywords) {
        this.keywords = searchBody.keywords;
      } else {
        this.keywords = null;
      }
      this.setFilterText(searchBody);
    });
  }

  public clearFilters() {
    this.store.dispatch(resetAdvancedSearchFilters({ onResetFilters: true }));
    this.setFilterText(null);
    this.store.dispatch(resetAuthorProfile());
    this.store.dispatch(resetOutletProfile());
    this.store.dispatch(resetTopicProfile());
  }

  public setFilterText(filterFields) {
    const filterArr = [];
    if (filterFields) {
      const {
        analysts,
        countries,
        timeframe,
        vendorOutlets,
        topAuthorsOnly,
        topOutletsOnly,
        authorFollowSelected,
        outletFollowSelected,
        outletGroups,
        authors,
        outlets,
        excludeAuthors,
        excludeOutlets,
        authorLabels,
        excludeAuthorLabels,
        outletLabels,
        excludeOutletLabels,
      } = filterFields;
      FILTERS.map((f: any) => {
        switch (f.key) {
          case 'outletGroups':
            if (outletGroups && outletGroups.length > 0) {
              const labels = outletGroups
                .map((groupId) =>
                  f.filters.find((item) => eq(item.value, groupId.toString()))
                )
                .filter(Boolean)
                .map(({ label }) => label);
              if (labels.length > 0) {
                filterArr.push('Outlet Groups: ' + labels.join(', '));
              }
            }
            break;

          case 'countries':
            if (countries) {
              const { label } = f.filters.find((item) =>
                eq(item.value, countries)
              );
              filterArr.push(label);
            }
            break;

          case 'vendorOutlets':
            if (vendorOutlets) {
              const { label } = f.filters.find((item) =>
                eq(item.value, vendorOutlets)
              );
              filterArr.push(label);
            }
            break;

          case 'timeframe':
            if (timeframe) {
              if (eq(timeframe, 'CUSTOM')) {
                const from = this.datePipe.transform(
                  filterFields.from,
                  'MM/dd/yyyy'
                );
                const to = this.datePipe.transform(
                  filterFields.to,
                  'MM/dd/yyyy'
                );
                const label = `${from}-${to}`;
                filterArr.push(label);
              } else {
                const { label } = f.filters.find((item) =>
                  eq(item.value, timeframe)
                );
                if (!eq(timeframe, 'ONE_YEAR')) {
                  filterArr.push(label);
                }
              }
            }
            break;

          case 'topAuthorsOnly':
            if (topAuthorsOnly) {
              const { label } = f.filters.find((item) =>
                eq(item.value, topAuthorsOnly)
              );
              filterArr.push(label);
            }
            break;

          case 'topOutletsOnly':
            if (topOutletsOnly) {
              const { label } = f.filters.find((item) =>
                eq(item.value, topOutletsOnly)
              );
              filterArr.push(label);
            }
            break;

          case 'authorFollowSelected':
            if (authorFollowSelected) {
              const { label } = f.filters.find((item) =>
                eq(item.value, authorFollowSelected)
              );
              filterArr.push(label);
            }
            break;

          case 'outletFollowSelected':
            if (outletFollowSelected) {
              const { label } = f.filters.find((item) =>
                eq(item.value, outletFollowSelected)
              );
              filterArr.push(label);
            }
            break;

          case 'authors':
            if (!isEmpty(authors)) {
              const label = `Include ${authors.length} author${authors.length > 1 ? 's' : ''}`;
              filterArr.push(label);
            }
            break;

          case 'outlets':
            if (!isEmpty(outlets)) {
              const label = `Include ${outlets.length} outlet${outlets.length > 1 ? 's' : ''}`;
              filterArr.push(label);
            }
            break;

          case 'excludeAuthors':
            if (!isEmpty(excludeAuthors)) {
              const label = `Exclude ${excludeAuthors.length} author${excludeAuthors.length > 1 ? 's' : ''}`;
              filterArr.push(label);
            }
            break;

          case 'excludeOutlets':
            if (!isEmpty(excludeOutlets)) {
              const label = `Exclude ${excludeOutlets.length} outlet${excludeOutlets.length > 1 ? 's' : ''}`;
              filterArr.push(label);
            }
            break;

          case 'authorLabels':
            if (!isEmpty(authorLabels)) {
              const label = `Include ${authorLabels.length} author categor${authorLabels.length > 1 ? 'ies' : 'y'}`;
              filterArr.push(label);
            }
            break;

          case 'excludeAuthorLabels':
            if (!isEmpty(excludeAuthorLabels)) {
              const label = `Exclude ${excludeAuthorLabels.length} author categor${excludeAuthorLabels.length > 1 ? 'ies' : 'y'}`;
              filterArr.push(label);
            }
            break;

          case 'outletLabels':
            if (!isEmpty(outletLabels)) {
              const label = `Include ${outletLabels.length} outlet categor${outletLabels.length > 1 ? 'ies' : 'y'}`;
              filterArr.push(label);
            }
            break;

          case 'excludeOutletLabels':
            if (!isEmpty(excludeOutletLabels)) {
              const label = `Exclude ${excludeOutletLabels.length} outlet categor${excludeOutletLabels.length > 1 ? 'ies' : 'y'}`;
              filterArr.push(label);
            }
            break;
        }
        this.filterText = join(filterArr, ', ');
        if (this.filterText) {
          this.store.dispatch(
            setAdvancedFilterText({ filterText: this.filterText })
          );
        }
      });
    } else {
      this.filterText = null;
      this.store.dispatch(unSetAdvancedFilterText());
    }
  }
}
